import {
	Badge,
	Button,
	Input,
	PaginatedDataTable,
	Tabs,
	TabsList,
	TabsTrigger,
	TabsTriggerWithNumber,
	useSidebar,
} from "@dune/ui";
import { RoleEnum } from "@dune/vars";
import type { Resource } from "@dune/types";
import { SearchSm } from "@untitled-ui/icons-react";
import type { ColumnDef } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useDeleteResourceMutation } from "../../api/mutations/resources.mutations";
import {
	getResourceFileUrl,
	useGetResources,
} from "../../api/queries/resources.queries";
import { useMe } from "../../api/queries/users.queries";
import ConfirmationDialog from "../../component/common/ConfirmationDialog";
import PageLayoutWithTabs from "../../component/layout/PageLayoutWithTabs";
import { useDebounce } from "../../hooks/useDebounce";
import { useDocumentSelection } from "../../hooks/panel-sync/useDocumentSelection";
import { usePagination } from "../../hooks/usePagination";
import { useDocumentStore } from "../../stores/document.store";
import { AddResourceDialog } from "./components/AddResourceDialog";
import { ResourceThemesTab } from "./components/ResourceThemesTab";
import { Download, FileText, Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { getResourcesColumns, getResourcesReadOnlyColumns } from "./resourcesColumns";
import { toast } from "sonner";
import { downloadFileWithName } from "../../utils/document.utils";
import { formatSimpleDate } from "@dune/utils";
import { resourceTypeLabels } from "@dune/vars";

type ResourceListTab = "all" | "documentation";
type DocumentationTableRow =
	| {
			id: string;
			rowType: "theme";
			themeName: string;
	  }
	| {
			id: string;
			rowType: "resource";
			resource: Resource;
	  };

const isResourceListTab = (value: string): value is ResourceListTab => {
	return value === "all" || value === "documentation";
};

const ResourcesListPage = () => {
	const [activeTab, setActiveTab] = useState("resources");
	const [resourceListTab, setResourceListTab] = useState<ResourceListTab>("all");
	const [searchInput, setSearchInput] = useState("");
	const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
	const [resourceToEdit, setResourceToEdit] = useState<Resource | null>(null);
	const [resourceToDelete, setResourceToDelete] = useState<Resource | null>(null);
	const [loadingDocumentId, setLoadingDocumentId] = useState<string | null>(null);
	const [downloadingDocumentId, setDownloadingDocumentId] = useState<
		string | null
	>(null);
	const debouncedSearch = useDebounce(searchInput, 500);
	const {
		page: allPage,
		limit: allLimit,
		handlePageChange: handleAllPageChange,
		handleLimitChange: handleAllLimitChange,
		setPage: setAllPage,
	} = usePagination();
	const {
		page: documentationPage,
		limit: documentationLimit,
		handlePageChange: handleDocumentationPageChange,
		handleLimitChange: handleDocumentationLimitChange,
		setPage: setDocumentationPage,
	} = usePagination();
	const { state } = useSidebar();
	const isCollapsed = state === "collapsed";
	const deleteResourceMutation = useDeleteResourceMutation();
	const { me } = useMe();
	const selectDocument = useDocumentStore((state) => state.selectDocument);
	const isDuneUser = me?.role === RoleEnum.DUNE;
	const isBrokerUser = me?.role === RoleEnum.BROKER;
	const canAccessResources = isDuneUser || isBrokerUser;

	useDocumentSelection({
		entityType: "BROKER",
		entityId: 0,
	});

	const { data: resourcesResponse, isLoading } = useGetResources(
		{
			page: allPage,
			limit: allLimit,
			search: debouncedSearch || undefined,
		},
		{
			enabled: canAccessResources,
		},
	);

	const { data: documentationResourcesResponse, isLoading: isLoadingDocumentations } =
		useGetResources(
			{
				page: documentationPage,
				limit: documentationLimit,
				search: debouncedSearch || undefined,
				type: "DOCUMENT",
			},
			{
				enabled: canAccessResources,
			},
		);

	const resources = resourcesResponse?.data ?? [];
	const paginationMeta = resourcesResponse?.meta ?? {
		page: allPage,
		limit: allLimit,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	};
	const documentationPaginationMeta = documentationResourcesResponse?.meta ?? {
		page: documentationPage,
		limit: documentationLimit,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	};

	const documentationResources = documentationResourcesResponse?.data ?? [];
	const allResourcesCount = paginationMeta.total;
	const documentationResourcesCount =
		documentationResourcesResponse?.meta?.total ?? documentationResources.length;

	const documentationsByTheme = useMemo(() => {
		const groupedByTheme = new Map<string, Resource[]>();
		for (const resource of documentationResources) {
			const themeName = resource.theme.name;
			const existingResources = groupedByTheme.get(themeName) ?? [];
			existingResources.push(resource);
			groupedByTheme.set(themeName, existingResources);
		}
		return Array.from(groupedByTheme.entries())
			.map(([themeName, resourcesByTheme]) => ({
				themeName,
				resourcesByTheme,
			}))
			.sort((firstTheme, secondTheme) =>
				firstTheme.themeName.localeCompare(secondTheme.themeName),
			);
	}, [documentationResources]);

	const documentationTableRows = useMemo<DocumentationTableRow[]>(() => {
		return documentationsByTheme.flatMap((documentationTheme) => {
			const themeRow: DocumentationTableRow = {
				id: `theme-${documentationTheme.themeName}`,
				rowType: "theme",
				themeName: documentationTheme.themeName,
			};
			const resourceRows: DocumentationTableRow[] =
				documentationTheme.resourcesByTheme.map((resource) => ({
					id: `resource-${resource.id}`,
					rowType: "resource",
					resource,
				}));
			return [themeRow, ...resourceRows];
		});
	}, [documentationsByTheme]);

	const handleDeleteResource = async () => {
		if (!resourceToDelete) {
			return;
		}
		await deleteResourceMutation.mutateAsync(resourceToDelete.id);
		setResourceToDelete(null);
	};

	const handleDownloadResource = useCallback(async (resource: Resource) => {
		const documentId = resource.id.toString();
		if (downloadingDocumentId === documentId) {
			return;
		}
		setDownloadingDocumentId(documentId);

		try {
			const fileUrl = await getResourceFileUrl(resource.id);
			await downloadFileWithName(fileUrl, resource.file.name);
		} catch {
			toast.error("Impossible de télécharger ce document pour le moment");
		} finally {
			setDownloadingDocumentId(null);
		}
	}, [downloadingDocumentId]);

	const resourcesColumns = useMemo(
		() =>
			getResourcesColumns({
				onEdit: (resource) => {
					setResourceToEdit(resource);
					setIsAddDialogOpen(true);
				},
				onDelete: (resource) => {
					setResourceToDelete(resource);
				},
				onDownload: handleDownloadResource,
				loadingDocumentId,
				downloadingDocumentId,
			}),
		[downloadingDocumentId, handleDownloadResource, loadingDocumentId],
	);

	const readOnlyResourcesColumns = useMemo(
		() =>
			getResourcesReadOnlyColumns({
				onDownload: handleDownloadResource,
				loadingDocumentId,
				downloadingDocumentId,
			}),
		[downloadingDocumentId, handleDownloadResource, loadingDocumentId],
	);

	const documentationColumns = useMemo<ColumnDef<DocumentationTableRow>[]>(
		() => [
			{
				accessorKey: "name",
				header: "Libellé",
				cell: ({ row }) => {
					const documentationRow = row.original;
					if (documentationRow.rowType === "theme") {
						return (
							<span className="font-semibold text-slate-700">
								{documentationRow.themeName}
							</span>
						);
					}
					const resource = documentationRow.resource;
					const isLoadingDocument = loadingDocumentId === resource.id.toString();

					return (
						<div className="flex items-center gap-3 min-w-0">
							{isLoadingDocument ? (
								<Loader2 size={16} className="animate-spin flex-shrink-0" />
							) : (
								<FileText size={16} className="flex-shrink-0" />
							)}
							<span className="truncate max-w-xs" title={resource.name}>
								{resource.name}
							</span>
						</div>
					);
				},
			},
			{
				accessorKey: "description",
				header: "Description",
				cell: ({ row }) => {
					const documentationRow = row.original;
					if (documentationRow.rowType === "theme") {
						return null;
					}
					return <span>{documentationRow.resource.description ?? "-"}</span>;
				},
			},
			{
				accessorKey: "type",
				header: "Type",
				cell: ({ row }) => {
					const documentationRow = row.original;
					if (documentationRow.rowType === "theme") {
						return null;
					}
					return (
						<Badge variant="secondary">
							{resourceTypeLabels[documentationRow.resource.type]}
						</Badge>
					);
				},
			},
			{
				accessorKey: "theme",
				header: "Thème",
				cell: ({ row }) => {
					const documentationRow = row.original;
					if (documentationRow.rowType === "theme") {
						return null;
					}
					return (
						<Badge variant="outline">
							{documentationRow.resource.theme.name}
						</Badge>
					);
				},
			},
			{
				accessorKey: "updatedAt",
				header: "Dernière modification",
				cell: ({ row }) => {
					const documentationRow = row.original;
					if (documentationRow.rowType === "theme") {
						return null;
					}
					return (
						<span>{formatSimpleDate(documentationRow.resource.updatedAt)}</span>
					);
				},
			},
			{
				accessorKey: "createdAt",
				header: "Création",
				cell: ({ row }) => {
					const documentationRow = row.original;
					if (documentationRow.rowType === "theme") {
						return null;
					}
					return (
						<span>{formatSimpleDate(documentationRow.resource.createdAt)}</span>
					);
				},
			},
			{
				id: "actions",
				header: "Actions",
				cell: ({ row }) => {
					const documentationRow = row.original;
					if (documentationRow.rowType === "theme") {
						return null;
					}
					const resource = documentationRow.resource;
					const isDownloadingDocument =
						downloadingDocumentId === resource.id.toString();

					return isDuneUser ? (
						<div className="flex items-center gap-1 justify-end">
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="h-8 w-8 p-0"
								aria-label="download"
								disabled={isDownloadingDocument}
								onClick={(event) => {
									event.stopPropagation();
									void handleDownloadResource(resource);
								}}
							>
								{isDownloadingDocument ? (
									<Loader2 size={14} className="animate-spin" />
								) : (
									<Download size={14} />
								)}
							</Button>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								onClick={(event) => {
									event.stopPropagation();
									setResourceToEdit(resource);
									setIsAddDialogOpen(true);
								}}
							>
								<Pencil className="h-4 w-4" />
							</Button>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								onClick={(event) => {
									event.stopPropagation();
									setResourceToDelete(resource);
								}}
							>
								<Trash2 className="h-4 w-4 text-red-600" />
							</Button>
						</div>
					) : (
						<div className="flex items-center gap-1">
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="h-8 w-8 p-0"
								aria-label="download"
								disabled={isDownloadingDocument}
								onClick={(event) => {
									event.stopPropagation();
									void handleDownloadResource(resource);
								}}
							>
								{isDownloadingDocument ? (
									<Loader2 size={14} className="animate-spin" />
								) : (
									<Download size={14} />
								)}
							</Button>
						</div>
					);
				},
			},
		],
		[
			downloadingDocumentId,
			handleDownloadResource,
			isDuneUser,
			loadingDocumentId,
		],
	);

	const handleResourceClick = async (resource: Resource) => {
		const documentId = resource.id.toString();
		if (loadingDocumentId === documentId) {
			return;
		}
		if (!resource.file.id) {
			return;
		}

		setLoadingDocumentId(documentId);
		selectDocument(
			"",
			resource.file.id,
			resource.file.mimeType,
			resource.file.name,
		);
		setLoadingDocumentId(null);
	};

	const roleBasedResourcesColumns = isDuneUser
		? resourcesColumns
		: readOnlyResourcesColumns;

	return (
		<PageLayoutWithTabs title="Gestion des ressources" tabContent={{}}>
			<div className="space-y-6 flex-1 min-h-0 flex flex-col">
				{isDuneUser && (
					<Tabs value={activeTab} onValueChange={setActiveTab}>
						<TabsList>
							<TabsTrigger value="resources" variant="compact">
								Ressources
							</TabsTrigger>
							<TabsTrigger value="themes" variant="compact">
								Thèmes
							</TabsTrigger>
						</TabsList>
					</Tabs>
				)}

				{!canAccessResources ? (
					<div className="text-sm text-gray-500">
						Vous n'avez pas accès à cette section.
					</div>
				) : isDuneUser && activeTab === "themes" ? (
					<div className="flex-1 min-h-0 w-full overflow-auto">
						<ResourceThemesTab />
					</div>
				) : (
					<>
						<div className="flex justify-between items-center mb-4 gap-3 flex-wrap">
							<div className="flex items-center gap-3 flex-wrap">
								<Tabs
									value={resourceListTab}
									onValueChange={(value) => {
										if (isResourceListTab(value)) {
											setResourceListTab(value);
										}
									}}
								>
									<TabsList>
										<TabsTriggerWithNumber
											value="all"
											variant="compact"
											number={allResourcesCount}
										>
											Tous
										</TabsTriggerWithNumber>
										<TabsTriggerWithNumber
											value="documentation"
											variant="compact"
											number={documentationResourcesCount}
										>
											Documentations
										</TabsTriggerWithNumber>
									</TabsList>
								</Tabs>
								<div className="w-80">
									<Input
										type="text"
										placeholder="Recherche rapide"
										value={searchInput}
										onChange={(event) => {
											setSearchInput(event.target.value);
											setAllPage(1);
											setDocumentationPage(1);
										}}
										iconOrText={<SearchSm />}
										iconOrTextPosition="right"
									/>
								</div>
							</div>
							{isDuneUser && (
								<Button
									onClick={() => {
										setResourceToEdit(null);
										setIsAddDialogOpen(true);
									}}
								>
									<Plus className="h-4 w-4" />
									Ajouter
								</Button>
							)}
						</div>

						<div className="flex-1 min-h-0 w-full overflow-auto">
							<div
								className={`${isCollapsed ? "max-w-[92vw]" : "max-w-[80vw]"} space-y-4`}
							>
								{resourceListTab === "all" ? (
									<PaginatedDataTable
										data={resources}
										columns={roleBasedResourcesColumns}
										meta={paginationMeta}
										onPageChange={handleAllPageChange}
										onLimitChange={handleAllLimitChange}
										onRowClick={handleResourceClick}
										isLoading={isLoading}
										noResultsText="Aucune ressource"
										fixedLayout
									/>
								) : (
									<PaginatedDataTable
										data={documentationTableRows}
										columns={documentationColumns}
										meta={documentationPaginationMeta}
										onPageChange={handleDocumentationPageChange}
										onLimitChange={handleDocumentationLimitChange}
										isLoading={isLoadingDocumentations}
										noResultsText="Aucune documentation"
										variant="static-clean"
										getRowClassName={(rowData) =>
											rowData.rowType === "theme"
												? "bg-slate-100 hover:bg-slate-100 cursor-default"
												: undefined
										}
										onRowClick={(rowData) => {
											if (rowData.rowType === "resource") {
												void handleResourceClick(rowData.resource);
											}
										}}
										fixedLayout
									/>
								)}
							</div>
						</div>
					</>
				)}
			</div>

			{isDuneUser && (
				<>
					<AddResourceDialog
						open={isAddDialogOpen}
						onOpenChange={(open) => {
							setIsAddDialogOpen(open);
							if (!open) {
								setResourceToEdit(null);
							}
						}}
						resourceToEdit={resourceToEdit}
					/>
					<ConfirmationDialog
						open={Boolean(resourceToDelete)}
						onOpenChange={(open) => {
							if (!open) {
								setResourceToDelete(null);
							}
						}}
						title="Supprimer la ressource"
						description={`Voulez-vous vraiment supprimer la ressource "${resourceToDelete?.name ?? ""}" ?`}
						confirmText="Supprimer"
						cancelText="Annuler"
						variant="danger"
						onConfirm={handleDeleteResource}
						isLoading={deleteResourceMutation.isPending}
					/>
				</>
			)}
		</PageLayoutWithTabs>
	);
};

export default ResourcesListPage;
